import { useState } from 'react';
import { contactOptionLabel, CUSTOM_CONTACT_VALUE } from '../../lib/projectDetailShared';
import type { ResolvedProjectContact } from '../../../shared/clientContact';
import { type ClientContactRow } from '../../../shared/types';
import { Dropdown } from '../../components/primitives';
import type { ProjectDetailRow } from './types';

export type ProjectContactPickerMode = 'picked' | 'custom' | 'default';

/** Fresh server data maps deterministically to the picker mode. The
    transient Custom transition is layered on top by ProjectRailBody. */
export function projectContactPickerMode(project: Pick<
  ProjectDetailRow,
  'contact_id' | 'contact_name' | 'contact_email' | 'contact_phone'
>): ProjectContactPickerMode {
  if (project.contact_id != null) return 'picked';
  if (project.contact_name != null || project.contact_email != null || project.contact_phone != null) return 'custom';
  return 'default';
}

export function contactPickerTransition(value: string): { contactId: string | null; transientCustom: boolean } {
  return value === CUSTOM_CONTACT_VALUE
    ? { contactId: null, transientCustom: true }
    : { contactId: value, transientCustom: false };
}

/** Same text as contactOptionLabel, but with "(default)" dimmed — used for
    the picker's closed-state summary. */
function ContactPillLabel({ contact, isDefault }: { contact: ClientContactRow; isDefault: boolean }) {
  return (
    <>
      {contact.name}
      {contact.role_label ? ` — ${contact.role_label}` : ''}
      {isDefault && <span style={{ color: 'var(--text-faint)' }}> (default)</span>}
    </>
  );
}

/** Contact rail picker (mockups-feature-builds §3b) — lists the client's
    live contacts (default first, as returned) plus a "Custom…" sentinel.
    Picking a contact PATCHes contact_id; picking "Custom…" clears it back
    to null so the free-text override ContactLine editors take over. */
function ContactPicker({
  project,
  clientContacts,
  resolvedContact,
  isCustom,
  onModeChange,
  patch,
}: {
  project: ProjectDetailRow;
  clientContacts: ClientContactRow[];
  resolvedContact: ResolvedProjectContact;
  isCustom: boolean;
  onModeChange: (custom: boolean) => void;
  patch: (fields: Record<string, unknown>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const defaultContact = clientContacts.find((c) => c.is_default === 1) ?? null;
  const pickedContact = project.contact_id
    ? (clientContacts.find((c) => c.id === project.contact_id) ?? null)
    : null;
  // Neither picked nor overridden: the client's live default is the ambient
  // selection (no write happens until the operator actually changes it).
  const selectedContact = pickedContact ?? (!isCustom ? defaultContact : null);

  return (
    <div className="field" data-rail-control="contact-picker">
      <div className="dropdown-anchor">
        <span
          className="select-pill"
          style={{ justifyContent: 'space-between', width: '100%' }}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="field-value" style={{ fontSize: 12.5 }}>
            {isCustom ? (
              'Custom…'
            ) : selectedContact ? (
              <ContactPillLabel contact={selectedContact} isDefault={selectedContact.id === defaultContact?.id} />
            ) : (
              (resolvedContact.name ?? 'Select a contact…')
            )}
          </span>
          <span className="caret">▾</span>
        </span>
        {open && (
          <Dropdown
            items={[
              ...clientContacts.map((c) => ({
                value: c.id,
                label: contactOptionLabel(c, c.id === defaultContact?.id),
              })),
              { value: CUSTOM_CONTACT_VALUE, label: 'Custom…' },
            ]}
            value={isCustom ? CUSTOM_CONTACT_VALUE : (selectedContact?.id ?? undefined)}
            onSelect={(value) => {
              const transition = contactPickerTransition(value);
              onModeChange(transition.transientCustom);
              void patch({ contact_id: transition.contactId });
            }}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

export { ContactPicker };
